import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useAuthStore } from './store/authStore';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

import HomePage from './pages/HomePage';
import GroupBuyListPage from './pages/GroupBuyListPage';
import GroupBuyDetailPage from './pages/GroupBuyDetailPage';
import ProductListPage from './pages/ProductListPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CheckoutPage from './pages/CheckoutPage';
import MyPage from './pages/MyPage';
import OrderDetailPage from './pages/OrderDetailPage';
import SupportPage from './pages/SupportPage';
import EventsListPage from './pages/EventsListPage';
import EventViewPage from './pages/EventViewPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ForgotIdPage from './pages/ForgotIdPage';
import PurchaseRequestPage from './pages/PurchaseRequestPage';
import FaqPage from './pages/FaqPage';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminGroupBuys from './pages/admin/AdminGroupBuys';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCategories from './pages/admin/AdminCategories';
import AdminBanner from './pages/admin/AdminBanner';
import AdminSiteEvents from './pages/admin/AdminSiteEvents';
import AdminPurchaseRequests from './pages/admin/AdminPurchaseRequests';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'ADMIN') return <Navigate to="/" replace />;
  return <>{children}</>;
};

const ShopLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div>
    <Header />
    <div style={{ minHeight: 'calc(100vh - 64px)' }}>{children}</div>
    <Footer />
  </div>
);

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
const PAYPAL_OPTIONS = { clientId: PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' };
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <PayPalScriptProvider options={PAYPAL_OPTIONS}>
    <BrowserRouter>
      <Routes>
        {/* Admin */}
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />
          <Route path="group-buys" element={<AdminGroupBuys />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="banner" element={<AdminBanner />} />
          <Route path="site-events" element={<AdminSiteEvents />} />
          <Route path="purchase-requests" element={<AdminPurchaseRequests />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="coupons" element={<AdminCoupons />} />
        </Route>

        {/* Public */}
        <Route path="/" element={<ShopLayout><HomePage /></ShopLayout>} />
        <Route path="/group-buys" element={<ShopLayout><GroupBuyListPage /></ShopLayout>} />
        <Route path="/group-buys/:code" element={<ShopLayout><GroupBuyDetailPage /></ShopLayout>} />
        <Route path="/products" element={<ShopLayout><ProductListPage /></ShopLayout>} />
        <Route path="/products/:id" element={<ShopLayout><ProductDetailPage /></ShopLayout>} />
        <Route path="/login" element={<ShopLayout><LoginPage /></ShopLayout>} />
        <Route path="/register" element={<ShopLayout><RegisterPage /></ShopLayout>} />
        <Route path="/faq" element={<ShopLayout><FaqPage /></ShopLayout>} />
        <Route path="/support" element={<ShopLayout><SupportPage /></ShopLayout>} />
        <Route path="/events" element={<ShopLayout><EventsListPage /></ShopLayout>} />
        <Route path="/events/:id" element={<ShopLayout><EventViewPage /></ShopLayout>} />
        <Route path="/welcome" element={<Navigate to="/events/welcome-coupon" replace />} />
        <Route path="/privacy" element={<ShopLayout><PrivacyPage /></ShopLayout>} />
        <Route path="/terms" element={<ShopLayout><TermsPage /></ShopLayout>} />
        <Route path="/forgot-password" element={<ShopLayout><ForgotPasswordPage /></ShopLayout>} />
        <Route path="/forgot-id" element={<ShopLayout><ForgotIdPage /></ShopLayout>} />

        {/* Auth required */}
        <Route path="/cart" element={<ShopLayout><RequireAuth><CartPage /></RequireAuth></ShopLayout>} />
        <Route path="/checkout" element={<ShopLayout><RequireAuth><CheckoutPage /></RequireAuth></ShopLayout>} />
        <Route path="/purchase-requests" element={<ShopLayout><PurchaseRequestPage /></ShopLayout>} />
        <Route path="/mypage" element={<ShopLayout><RequireAuth><MyPage /></RequireAuth></ShopLayout>} />
        <Route path="/orders/:id" element={<ShopLayout><RequireAuth><OrderDetailPage /></RequireAuth></ShopLayout>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </PayPalScriptProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
